import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import api, { watiApi, exportApi, settingsApi, aiApi, usersApi, slaApi, zohoApi, adminApi } from "../lib/api";
import { toast } from "sonner";
import {
  User,
  Bell,
  RefreshCw,
  Download,
  MessageSquare,
  Shield,
  Loader2,
  CheckCircle,
  Clock,
  Users,
  Brain,
  Zap,
  Save,
  Sparkles,
  Plus,
  X,
  ChevronDown,
  Timer,
  Trash2,
  ExternalLink,
  GitBranch,
  Send,
} from "lucide-react";
import axios from "axios";
import PushNotificationsCard from "../components/PushNotificationsCard";
import SmtpSettingsCard from "../components/SmtpSettingsCard";
import DuplicateLeadsCard from "../components/DuplicateLeadsCard";
import MisroutedCsAuditCard from "../components/MisroutedCsAuditCard";
import BroadcastDedupCard from "../components/BroadcastDedupCard";
import TelecmiChubCard from "../components/TelecmiChubCard";
import SlackAlertsCard from "../components/SlackAlertsCard";
import CurrencyRatesCard from "../components/CurrencyRatesCard";
import MockCallStarterCard from "../components/MockCallStarterCard";
import SettingsAiSection from "../components/settings/SettingsAiSection";
import SettingsSalesCard from "../components/settings/SettingsSalesCard";
import SettingsWatiCard from "../components/settings/SettingsWatiCard";
import SettingsZohoCard from "../components/settings/SettingsZohoCard";
import SettingsRazorpayCard from "../components/settings/SettingsRazorpayCard";

const API = process.env.REACT_APP_BACKEND_URL;


// Multi-select agent picker with tags
const AgentTagSelector = ({ selected, allUsers, onChange, testId }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const availableUsers = allUsers.filter(u => u.role !== "admin" && !selected.includes(u.id));

  const addAgent = (userId) => {
    onChange([...selected, userId]);
    setShowDropdown(false);
  };
  const removeAgent = (userId) => {
    onChange(selected.filter(id => id !== userId));
  };

  return (
    <div className="relative" data-testid={`${testId}-selector`}>
      <div className="flex flex-wrap gap-1.5 min-h-[36px] bg-white border border-gray-200 rounded-lg px-2 py-1.5 items-center">
        {selected.map(agentId => {
          const u = allUsers.find(usr => usr.id === agentId || usr.email === agentId);
          const label = u ? u.name : agentId;
          return (
            <span key={agentId} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full border border-blue-200" data-testid={`${testId}-tag-${agentId}`}>
              {label}
              <button onClick={() => removeAgent(agentId)} className="hover:text-red-500 ml-0.5" data-testid={`${testId}-remove-${agentId}`}><X size={11} /></button>
            </span>
          );
        })}
        {availableUsers.length > 0 && (
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="text-xs text-[#57534E] hover:text-blue-600 flex items-center gap-0.5 px-1"
            data-testid={`${testId}-add-btn`}
          >
            <Plus size={12} /> Add
          </button>
        )}
      </div>
      {showDropdown && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} />
          <div className="absolute left-0 top-full mt-1 z-50 bg-white rounded-lg shadow-xl border border-gray-200 py-1 w-64 max-h-48 overflow-y-auto" data-testid={`${testId}-dropdown`}>
            {availableUsers.map(u => (
              <button
                key={u.id}
                onClick={() => addAgent(u.id)}
                className="flex items-center gap-2 w-full px-3 py-2 text-xs text-left hover:bg-blue-50 transition-colors"
                data-testid={`${testId}-option-${u.id}`}
              >
                <User size={12} className="text-gray-400" />
                <span className="font-medium">{u.name}</span>
                <span className="text-gray-400 ml-auto">{u.email}</span>
              </button>
            ))}
            {availableUsers.length === 0 && (
              <p className="text-xs text-gray-400 px-3 py-2">No more agents to add</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const SettingsPage = () => {
  const { user } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [syncingMessages, setSyncingMessages] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [watiStatus, setWatiStatus] = useState(null);
  const [assignmentRules, setAssignmentRules] = useState(null);
  const [savingRules, setSavingRules] = useState(false);
  const [newCatKey, setNewCatKey] = useState("");
  const [newCatAgent, setNewCatAgent] = useState("");
  const [aiKnowledge, setAiKnowledge] = useState(null);
  const [learningInProgress, setLearningInProgress] = useState(false);
  const [aiRules, setAiRules] = useState(null);
  const [aiRulesText, setAiRulesText] = useState("");
  const [savingAiRules, setSavingAiRules] = useState(false);
  const [refreshingStatuses, setRefreshingStatuses] = useState(false);
  const [statusRefreshProgress, setStatusRefreshProgress] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [slaConfig, setSlaConfig] = useState({ enabled: false, first_response_minutes: 15, resolution_hours: 24 });
  const [savingSla, setSavingSla] = useState(false);
  const [autoReply, setAutoReply] = useState({ enabled: true, schedule: { days: [0], all_day: true, start_hour: 0, end_hour: 24 }, transfer_message: "" });
  const [savingAutoReply, setSavingAutoReply] = useState(false);
  const [priorityQueue, setPriorityQueue] = useState(false);

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "super_admin") {
      fetchWatiStatus();
      fetchAssignmentRules();
      fetchAiKnowledge();
      fetchAiRules();
      fetchAllUsers();
      fetchSlaConfig();
      fetchAutoReply();
      fetchPriorityQueue();
      // Auto-refresh webhook health every 15s so admin can watch it go live
      const t = setInterval(fetchWatiStatus, 15000);
      return () => clearInterval(t);
    }
    // All `fetch*` callbacks are stable component-local functions that read
    // fresh props via closure. Including them in deps would cause the effect
    // to re-run on every render (they're recreated each time), leading to
    // infinite data-fetch loops. The only value that should actually trigger
    // a refetch is the user's role — when that changes, the effect re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const fetchAllUsers = async () => {
    try {
      const res = await usersApi.getAll();
      setAllUsers(res.data.users || []);
    } catch (e) { console.debug("settings: fetchAllUsers non-blocking failure", e); }
  };

  // Non-admin users — used twice in the assignment-rules JSX. Memoised so we
  // don't re-filter `allUsers` on every keystroke.
  const nonAdminUsers = useMemo(
    () => (allUsers || []).filter(u => u.role !== "admin"),
    [allUsers]
  );

  const fetchSlaConfig = async () => {
    try {
      const res = await slaApi.getConfig();
      setSlaConfig(res.data);
    } catch (e) { console.debug("settings: non-blocking config fetch failed", e); }
  };

  const saveSlaConfig = async () => {
    setSavingSla(true);
    try {
      await slaApi.updateConfig(slaConfig);
      toast.success("SLA config saved");
    } catch (err) { toast.error("Failed to save SLA config"); }
    finally { setSavingSla(false); }
  };

  const fetchAutoReply = async () => {
    try {
      const res = await settingsApi.getAutoReply();
      setAutoReply(res.data);
    } catch (e) { console.debug("settings: non-blocking config fetch failed", e); }
  };

  const saveAutoReply = async () => {
    setSavingAutoReply(true);
    try {
      await settingsApi.updateAutoReply(autoReply);
      toast.success("Auto-reply settings saved");
    } catch (err) { toast.error("Failed to save"); }
    finally { setSavingAutoReply(false); }
  };

  const fetchPriorityQueue = async () => {
    try {
      const res = await settingsApi.getPriorityQueue();
      setPriorityQueue(res.data.enabled);
    } catch (e) { console.debug("settings: non-blocking config fetch failed", e); }
  };

  const togglePriorityQueue = async () => {
    try {
      const newVal = !priorityQueue;
      await settingsApi.updatePriorityQueue({ enabled: newVal });
      setPriorityQueue(newVal);
      toast.success(newVal ? "Smart Priority Queue enabled" : "Smart Priority Queue disabled");
    } catch (err) { toast.error("Failed to update"); }
  };

  const fetchAssignmentRules = async () => {
    try {
      const res = await settingsApi.getAssignmentRules();
      setAssignmentRules(res.data);
    } catch (e) { console.debug("settings: non-blocking config fetch failed", e); }
  };

  const saveAssignmentRules = async () => {
    setSavingRules(true);
    try {
      await settingsApi.updateAssignmentRules(assignmentRules);
      toast.success("Assignment rules saved");
    } catch (err) { toast.error("Failed to save rules"); }
    finally { setSavingRules(false); }
  };

  const fetchAiKnowledge = async () => {
    try {
      const res = await aiApi.getKnowledge();
      setAiKnowledge(res.data);
    } catch (e) { console.debug("settings: non-blocking config fetch failed", e); }
  };

  const fetchAiRules = async () => {
    try {
      const res = await aiApi.getRules();
      setAiRules(res.data);
      setAiRulesText(res.data?.text || "");
    } catch (e) { console.debug("settings: non-blocking config fetch failed", e); }
  };

  const handleSaveAiRules = async () => {
    setSavingAiRules(true);
    try {
      await aiApi.saveRules(aiRulesText);
      toast.success("AI rules saved — all future replies will follow them");
      fetchAiRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save rules");
    } finally {
      setSavingAiRules(false);
    }
  };

  const handleRefreshAllWatiStatuses = async () => {
    if (!window.confirm("Refresh ticket statuses for ALL leads from WATI? This can take several minutes.")) return;
    setRefreshingStatuses(true);
    setStatusRefreshProgress(null);
    try {
      const res = await watiApi.refreshAllStatuses("all");
      const jobId = res.data.job_id;
      toast.success("Refresh started — this runs in background");
      // Iter 152.22e (May 2026, Yash) — robust polling.
      // Previously: poll never stopped on errors → spinner stuck forever.
      // Now: stop on `done`/`failed`, escalate after 5 consecutive errors,
      // and hard-cap at 30 minutes (3s × 600).
      let consecutiveErrors = 0;
      let pollCount = 0;
      const MAX_POLLS = 600; // ≈ 30 min at 3s
      const poll = setInterval(async () => {
        pollCount += 1;
        if (pollCount > MAX_POLLS) {
          clearInterval(poll);
          setRefreshingStatuses(false);
          toast.error("Refresh polling timed out after 30 min — check Settings page later for the final result");
          return;
        }
        try {
          const p = await watiApi.refreshStatusProgress(jobId);
          consecutiveErrors = 0;
          setStatusRefreshProgress(p.data);
          if (p.data?.status === "done") {
            clearInterval(poll);
            setRefreshingStatuses(false);
            toast.success(`Done: ${p.data.leads_updated} leads updated${p.data.errors ? ` · ${p.data.errors} errors` : ""}`);
          } else if (p.data?.status === "failed") {
            clearInterval(poll);
            setRefreshingStatuses(false);
            toast.error(p.data.error || "Refresh failed");
          }
        } catch (e) {
          consecutiveErrors += 1;
          if (consecutiveErrors >= 5) {
            clearInterval(poll);
            setRefreshingStatuses(false);
            toast.error("Lost connection to refresh job — check Settings page later");
          }
        }
      }, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start refresh");
      setRefreshingStatuses(false);
    }
  };

  const [mergingDupes, setMergingDupes] = useState(false);
  const [dupeCheckResult, setDupeCheckResult] = useState(null);
  const [reroutingAll, setReroutingAll] = useState(false);
  const [rerouteResult, setRerouteResult] = useState(null);
  const [reclassifyingBc, setReclassifyingBc] = useState(false);
  const [reclassifyResult, setReclassifyResult] = useState(null);
  const [repairingMedia, setRepairingMedia] = useState(false);
  const [mediaRepairResult, setMediaRepairResult] = useState(null);
  const [auditingMedia, setAuditingMedia] = useState(false);
  const [mediaAudit, setMediaAudit] = useState(null);

  const handleMediaAudit = async () => {
    setAuditingMedia(true);
    setMediaAudit(null);
    try {
      const res = await api.get("/admin/wati/media-audit");
      setMediaAudit(res.data);
      toast.success(`Audited ${res.data?.total ?? 0} media rows`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed");
    } finally {
      setAuditingMedia(false);
    }
  };

  const handleRepairMedia = async () => {
    if (!window.confirm("Repair broken-media URLs by re-resolving them via WATI? Runs slowly (gentle pacing) so we don't trip rate limits.")) return;
    setRepairingMedia(true);
    setMediaRepairResult(null);
    try {
      const res = await adminApi.repairMedia(500);
      setMediaRepairResult(res.data);
      toast.success(res.data?.message || `Repaired ${res.data?.prefix_fixed ?? 0} rows instantly; ${res.data?.queued_for_background_lookup ?? 0} queued for lookup.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run media repair");
    } finally {
      setRepairingMedia(false);
    }
  };

  // Zoho Desk (email ticketing) integration
  const [zohoStatus, setZohoStatus] = useState(null);
  const [zohoCode, setZohoCode] = useState("");
  const [zohoAuthLoading, setZohoAuthLoading] = useState(false);

  // Razorpay — DB-backed credentials so admins can set them on any environment.
  const [razorpayStatus, setRazorpayStatus] = useState(null);
  const [razorpayKeyId, setRazorpayKeyId] = useState("");
  const [razorpayKeySecret, setRazorpayKeySecret] = useState("");
  const [savingRazorpay, setSavingRazorpay] = useState(false);
  const [showRazorpayForm, setShowRazorpayForm] = useState(false);

  const fetchRazorpayStatus = async () => {
    try {
      const res = await settingsApi.getRazorpay();
      setRazorpayStatus(res.data);
    } catch (err) {
      console.warn("[settings] Razorpay status fetch failed:", err?.message || err);
      setRazorpayStatus({ configured: false });
    }
  };

  const saveRazorpay = async () => {
    const k = razorpayKeyId.trim();
    const s = razorpayKeySecret.trim();
    if (!k || !s) { toast.error("Both Key ID and Key Secret are required"); return; }
    setSavingRazorpay(true);
    try {
      await settingsApi.updateRazorpay({ key_id: k, key_secret: s });
      toast.success("Razorpay keys saved ✓");
      setRazorpayKeyId(""); setRazorpayKeySecret("");
      setShowRazorpayForm(false);
      await fetchRazorpayStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingRazorpay(false);
    }
  };

  const clearRazorpay = async () => {
    if (!window.confirm("Remove Razorpay credentials from this environment? Payment links will stop working until new keys are saved.")) return;
    setSavingRazorpay(true);
    try {
      await settingsApi.updateRazorpay({ key_id: "", key_secret: "" });
      toast.success("Razorpay credentials cleared");
      await fetchRazorpayStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed");
    } finally {
      setSavingRazorpay(false);
    }
  };

  // Team-notify config — recipients + reasons used by the chat-header pill button
  const [teamNotify, setTeamNotify] = useState({ recipients: [], reasons: [] });
  const [savingTeamNotify, setSavingTeamNotify] = useState(false);

  // Stable per-row id so React's reconciler doesn't re-key the inputs when
  // the user edits a label / removes a row in the middle. Row-index keys
  // cause focus loss + state bleed; a uuid attached on hydrate or add
  // survives every mutation.
  const _withUid = (rows) =>
    (rows || []).map((r) => (r && r._uid ? r : {
      ...r,
      _uid: (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `uid-${Math.random().toString(36).slice(2)}-${Date.now()}`,
    }));

  const fetchTeamNotify = async () => {
    try {
      const res = await settingsApi.getTeamNotify();
      const data = res.data || { recipients: [], reasons: [] };
      setTeamNotify({
        recipients: _withUid(data.recipients),
        reasons: _withUid(data.reasons),
      });
    } catch (err) {
      // Defaults will seed on first save — fetch failure is non-blocking.
      console.debug("[settings] team-notify fetch failed:", err?.message || err);
    }
  };

  const saveTeamNotify = async () => {
    setSavingTeamNotify(true);
    try {
      // Strip the frontend-only `_uid` before posting — it's only used for
      // React reconciliation, the backend doesn't expect it.
      const stripUid = ({ _uid, ...rest }) => rest;
      const cleaned = {
        recipients: (teamNotify.recipients || [])
          .filter((r) => (r.label || "").trim() && (r.email || "").includes("@"))
          .map(stripUid),
        reasons: (teamNotify.reasons || [])
          .filter((r) => (r.value || "").trim() && (r.label || "").trim())
          .map(stripUid),
      };
      if (!cleaned.recipients.length) { toast.error("Add at least one recipient"); return; }
      if (!cleaned.reasons.length) { toast.error("Add at least one reason"); return; }
      await settingsApi.updateTeamNotify(cleaned);
      toast.success("Team notifications saved ✓");
      await fetchTeamNotify();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingTeamNotify(false);
    }
  };

  const fetchZohoStatus = async () => {
    try {
      const res = await zohoApi.getStatus();
      setZohoStatus(res.data);
    } catch (err) {
      setZohoStatus(null);
    }
  };

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "super_admin") {
      fetchZohoStatus();
      fetchRazorpayStatus();
      fetchTeamNotify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const handleZohoExchange = async () => {
    const code = zohoCode.trim();
    if (!code) {
      toast.error("Paste the Grant Token (Code) first");
      return;
    }
    setZohoAuthLoading(true);
    try {
      await zohoApi.exchangeCode(code);
      toast.success("Zoho Desk authorized successfully");
      setZohoCode("");
      await fetchZohoStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zoho authorization failed");
    } finally {
      setZohoAuthLoading(false);
    }
  };

  const ZOHO_SCOPE_STRING = "Desk.tickets.ALL,Desk.contacts.READ,Desk.basic.READ,Desk.settings.READ,Desk.search.READ,Desk.tasks.READ";

  const handleZohoReauthorize = async () => {
    // Two-step helper for Self Client users:
    // 1. Copy the full scope string to clipboard (with Desk.tickets.ALL — replaces read-only scopes)
    // 2. Open Zoho API console's Generate Code tab in a new window
    try {
      await navigator.clipboard.writeText(ZOHO_SCOPE_STRING);
      toast.success("Scope copied. Opening Zoho API console — paste the scope, duration 10 min, click Create, then paste the code back here.", { duration: 9000 });
    } catch (err) {
      toast.info("Opening Zoho API console. Copy this scope: " + ZOHO_SCOPE_STRING, { duration: 15000 });
    }
    const dc = (window.__ZOHO_DATA_CENTER__ || "in"); // env-set at build not available; default IN
    window.open(`https://api-console.zoho.${dc}/`, "_blank", "noopener,noreferrer");
  };

  const handleCheckDuplicates = async () => {
    setMergingDupes(true);
    try {
      const res = await watiApi.mergeDuplicateLeads(true);  // dry run
      setDupeCheckResult(res.data);
      if (res.data.duplicate_phone_count === 0) {
        toast.success("No duplicate leads found ✓");
      } else {
        toast.info(`Found ${res.data.duplicate_phone_count} phones with duplicates (${res.data.extra_rows_to_delete} extra rows)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to check duplicates");
    } finally {
      setMergingDupes(false);
    }
  };

  const handleMergeDuplicates = async () => {
    if (!dupeCheckResult || dupeCheckResult.duplicate_phone_count === 0) return;
    if (!window.confirm(`Merge ${dupeCheckResult.duplicate_phone_count} duplicate phone groups? This will delete ${dupeCheckResult.extra_rows_to_delete} duplicate lead rows and move their messages to the canonical (oldest) lead. This action cannot be undone.`)) return;
    setMergingDupes(true);
    try {
      const res = await watiApi.mergeDuplicateLeads(false);
      toast.success(`Merged ${res.data.leads_merged_and_deleted} duplicate leads · ${res.data.messages_moved} messages moved`);
      setDupeCheckResult({ duplicate_phone_count: 0, extra_rows_to_delete: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally {
      setMergingDupes(false);
    }
  };

  // Bulk re-route: re-run CS-keyword classifier on every currently-open lead
  // in Sales/no-department. Useful after expanding the keyword list.
  const handleRerouteAll = async () => {
    if (!window.confirm("Re-run the Customer Support classifier on every currently-open Sales lead (scans last 5 customer messages each)?\n\nLeads where a CS keyword matches will flip to Customer Support. Safe — never closes / solves tickets.")) return;
    setReroutingAll(true);
    setRerouteResult(null);
    try {
      const res = await adminApi.rerouteAllOpen();
      setRerouteResult(res.data);
      toast.success(`Scanned ${res.data.scanned} · ${res.data.rerouted} flipped to Customer Support · ${res.data.errors} errors`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk re-route failed");
    } finally {
      setReroutingAll(false);
    }
  };

  // Retroactive fix for broadcasts that were stored as `direction: incoming`
  // (pre-Session-18r6 classifier was too lenient). Flips them to outgoing +
  // is_broadcast and reverts any wrongful auto-reopens.
  const handleReclassifyBroadcasts = async () => {
    if (!window.confirm("Scan existing messages and re-classify broadcast/template bodies that were wrongly stored as incoming (from the customer)?\n\nAny lead that was auto-reopened purely because of a misclassified broadcast will be reverted to closed. Safe idempotent — running twice changes nothing the second time.")) return;
    setReclassifyingBc(true);
    setReclassifyResult(null);
    try {
      const res = await adminApi.reclassifyBroadcasts();
      setReclassifyResult(res.data);
      toast.success(`Scanned ${res.data.scanned} · ${res.data.flipped_to_broadcast} re-classified · ${res.data.leads_reverted_to_closed} leads reverted to closed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-classify failed");
    } finally {
      setReclassifyingBc(false);
    }
  };

  const triggerAiLearning = async () => {
    setLearningInProgress(true);
    try {
      const res = await aiApi.triggerLearning();
      toast.success(res.data.message || "AI learning complete");
      fetchAiKnowledge();
    } catch (e) { toast.error(e.response?.data?.detail || "Learning failed"); }
    finally { setLearningInProgress(false); }
  };

  const fetchWatiStatus = async () => {
    try {
      const response = await watiApi.getStatus();
      setWatiStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch WATI status:", error);
    }
  };

  const handleSyncWati = async () => {
    setSyncing(true);
    try {
      const response = await watiApi.sync();
      toast.success(response.data.message || "WATI sync complete");
      fetchWatiStatus(); // Refresh status after sync
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync with WATI");
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncMessages = async () => {
    setSyncingMessages(true);
    try {
      const response = await watiApi.syncMessages();
      toast.success(response.data.message || "Messages synced successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync messages");
    } finally {
      setSyncingMessages(false);
    }
  };

  const handleExportOrders = async () => {
    setExporting(true);
    try {
      const response = await exportApi.orders();
      const orders = response.data.orders;

      // Convert to CSV
      const headers = [
        "Order Number",
        "Amount",
        "Category",
        "Lead ID",
        "Salesperson ID",
        "Created At",
      ];
      const rows = orders.map((o) => [
        o.order_number,
        o.amount,
        o.category,
        o.lead_id,
        o.salesperson_id,
        o.created_at,
      ]);

      const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders_export_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Orders exported successfully");
    } catch (error) {
      toast.error("Failed to export orders");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1
          className="text-3xl font-medium text-[#1C1917]"
          style={{ fontFamily: "Playfair Display, serif" }}
        >
          Settings
        </h1>
        <p className="text-[#57534E] mt-1">
          Manage your account and application settings
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Card */}
        <div className="bento-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[#312E81]/10 rounded-md">
              <User size={20} className="text-[#312E81]" />
            </div>
            <h2
              className="text-xl font-medium text-[#1C1917]"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Profile
            </h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-[#F5F2EA] rounded-md">
              <div className="avatar w-16 h-16 text-xl">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <div>
                <p className="font-medium text-lg text-[#1C1917]">{user?.name}</p>
                <p className="text-[#57534E]">{user?.email}</p>
                <span
                  className={`mt-1 inline-block px-2 py-0.5 text-xs uppercase tracking-wider rounded-sm ${
                    user?.role === "admin" || user?.role === "super_admin"
                      ? "bg-[#BC4B35]/10 text-[#BC4B35]"
                      : user?.role === "leader"
                      ? "bg-[#312E81]/10 text-[#312E81]"
                      : "bg-[#15803D]/10 text-[#15803D]"
                  }`}
                >
                  {user?.role}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Role Permissions Card */}
        <div className="bento-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[#D97706]/10 rounded-md">
              <Shield size={20} className="text-[#D97706]" />
            </div>
            <h2
              className="text-xl font-medium text-[#1C1917]"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Role Permissions
            </h2>
          </div>

          <div className="space-y-3">
            <div className="p-3 bg-[#F5F2EA] rounded-md">
              <p className="font-medium text-[#1C1917]">Your Access Level</p>
              <p className="text-sm text-[#57534E] mt-1">
                {(user?.role === "admin" || user?.role === "super_admin") && (
                  <>Full access: Manage users, view all data, export reports</>
                )}
                {user?.role === "leader" && (
                  <>Team access: View team performance, assign leads, export reports</>
                )}
                {user?.role === "salesperson" && (
                  <>Standard access: View assigned leads, manage orders & reminders</>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Push Notifications Card — available to every logged-in user */}
        <PushNotificationsCard />

        {/* SMTP (Outbound Email) — admin-only */}
        {(user?.role === "admin" || user?.role === "super_admin") && <SmtpSettingsCard />}

        {/* WATI Integration Card */}
        {/* WATI Integration Card — extracted to components/settings/SettingsWatiCard.jsx (iter 138, Feb 2026) */}
        <SettingsWatiCard
          user={user}
          watiStatus={watiStatus}
          syncing={syncing}
          handleSyncWati={handleSyncWati}
          syncingMessages={syncingMessages}
          handleSyncMessages={handleSyncMessages}
          dupeCheckResult={dupeCheckResult}
          mergingDupes={mergingDupes}
          handleCheckDuplicates={handleCheckDuplicates}
          handleMergeDuplicates={handleMergeDuplicates}
          reroutingAll={reroutingAll}
          handleRerouteAll={handleRerouteAll}
          rerouteResult={rerouteResult}
          reclassifyingBc={reclassifyingBc}
          handleReclassifyBroadcasts={handleReclassifyBroadcasts}
          reclassifyResult={reclassifyResult}
          repairingMedia={repairingMedia}
          handleRepairMedia={handleRepairMedia}
          mediaRepairResult={mediaRepairResult}
          handleRefreshAllWatiStatuses={handleRefreshAllWatiStatuses}
          refreshingStatuses={refreshingStatuses}
          statusRefreshProgress={statusRefreshProgress}
        />
        {/* Zoho card extracted to components/settings/SettingsZohoCard.jsx (iter 140) */}
        <SettingsZohoCard
          user={user}
          zohoStatus={zohoStatus}
          zohoCode={zohoCode}
          setZohoCode={setZohoCode}
          zohoAuthLoading={zohoAuthLoading}
          handleZohoExchange={handleZohoExchange}
          handleZohoReauthorize={handleZohoReauthorize}
          fetchZohoStatus={fetchZohoStatus}
        />

        {/* Razorpay card extracted to components/settings/SettingsRazorpayCard.jsx (iter 140) */}
        <SettingsRazorpayCard
          user={user}
          razorpayStatus={razorpayStatus}
          razorpayKeyId={razorpayKeyId}
          setRazorpayKeyId={setRazorpayKeyId}
          razorpayKeySecret={razorpayKeySecret}
          setRazorpayKeySecret={setRazorpayKeySecret}
          saveRazorpay={saveRazorpay}
          savingRazorpay={savingRazorpay}
          clearRazorpay={clearRazorpay}
          showRazorpayForm={showRazorpayForm}
          setShowRazorpayForm={setShowRazorpayForm}
          exporting={exporting}
          handleExportOrders={handleExportOrders}
          teamNotify={teamNotify}
          setTeamNotify={setTeamNotify}
          saveTeamNotify={saveTeamNotify}
          savingTeamNotify={savingTeamNotify}
        />
        {/* Notifications Card */}
        <div className="bento-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[#312E81]/10 rounded-md">
              <Bell size={20} className="text-[#312E81]" />
            </div>
            <h2
              className="text-xl font-medium text-[#1C1917]"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Notifications
            </h2>
          </div>

          <div className="space-y-3">
            <p className="text-[#57534E]">
              Notification preferences will be available in a future update.
              Currently, all reminder notifications appear on your dashboard.
            </p>
          </div>
        </div>

        {/* Assignment Rules Card - Admin Only */}
        {(user?.role === "admin" || user?.role === "super_admin") && assignmentRules && (
          <div className="bento-card lg:col-span-2" data-testid="assignment-rules-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-md"><Users size={20} className="text-blue-700" /></div>
                <h2 className="text-xl font-medium text-[#1C1917]" style={{ fontFamily: "Playfair Display, serif" }}>Auto-Assignment Rules</h2>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={assignmentRules.enabled} onChange={(e) => setAssignmentRules(r => ({ ...r, enabled: e.target.checked }))} className="w-4 h-4 rounded border-gray-300 text-blue-600" data-testid="rules-enabled-toggle" />
                  Enabled
                </label>
                <button onClick={saveAssignmentRules} disabled={savingRules} className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1" data-testid="save-rules-btn">
                  {savingRules ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Save</>}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Category Assignments */}
              <div>
                <h3 className="text-sm font-semibold text-[#1C1917] mb-2">Category Assignments</h3>
                <p className="text-xs text-[#57534E] mb-2">Assign categories to specific agents</p>
                <div className="space-y-1.5">
                  {Object.entries(assignmentRules.category_assignments || {}).map(([cat, agentId]) => {
                    const agentUser = allUsers.find(u => u.id === agentId || u.email === agentId);
                    return (
                      <div key={cat} className="flex items-center gap-2 bg-[#F5F2EA] rounded-lg px-3 py-1.5">
                        <span className="text-sm font-medium min-w-[80px]">{cat}</span>
                        <select
                          value={agentId}
                          onChange={(e) => setAssignmentRules(r => ({
                            ...r, category_assignments: { ...r.category_assignments, [cat]: e.target.value }
                          }))}
                          className="flex-1 text-xs bg-white border border-gray-200 rounded px-2 py-1.5 outline-none"
                          data-testid={`cat-agent-${cat}`}
                        >
                          <option value="">-- Select Agent --</option>
                          {nonAdminUsers.map(u => (
                            <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                          ))}
                        </select>
                        <button onClick={() => setAssignmentRules(r => {
                          const ca = { ...r.category_assignments };
                          delete ca[cat];
                          return { ...r, category_assignments: ca };
                        })} className="text-red-400 hover:text-red-600" data-testid={`remove-cat-${cat}`}><X size={14} /></button>
                      </div>
                    );
                  })}
                  <div className="flex items-center gap-2 mt-2">
                    <input type="text" value={newCatKey} onChange={(e) => setNewCatKey(e.target.value)} placeholder="Category name" className="text-xs border border-gray-200 rounded px-2 py-1.5 flex-1" data-testid="new-cat-key" />
                    <select value={newCatAgent} onChange={(e) => setNewCatAgent(e.target.value)} className="text-xs border border-gray-200 rounded px-2 py-1.5 w-48 outline-none" data-testid="new-cat-agent">
                      <option value="">-- Select Agent --</option>
                      {nonAdminUsers.map(u => (
                        <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                      ))}
                    </select>
                    <button onClick={() => {
                      if (newCatKey && newCatAgent) {
                        setAssignmentRules(r => ({ ...r, category_assignments: { ...r.category_assignments, [newCatKey]: newCatAgent } }));
                        setNewCatKey(""); setNewCatAgent("");
                      }
                    }} className="btn-secondary text-xs py-1.5 px-2" data-testid="add-cat-rule-btn"><Plus size={12} /></button>
                  </div>
                </div>
              </div>

              {/* Round-Robin Pools */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-[#1C1917] mb-1">Books Agents (Round-Robin)</h3>
                  <p className="text-xs text-[#57534E] mb-2">Book queries are assigned alternately to these agents</p>
                  <AgentTagSelector
                    selected={assignmentRules.books_agents || []}
                    allUsers={allUsers}
                    onChange={(agents) => setAssignmentRules(r => ({ ...r, books_agents: agents }))}
                    testId="books-agents"
                  />
                  {/* Iter 139 (Feb 2026) — Yash: Heena left, Sumit alone on Books.
                      Let some queries go to AI (Arjun) so Sumit isn't overloaded. */}
                  <div className="mt-3 p-3 bg-orange-50 border border-orange-100 rounded-lg" data-testid="books-ai-share-row">
                    <label className="text-xs font-bold text-orange-800 mb-1 block">
                      AI takes <input
                        type="number" min={0} max={100}
                        value={(assignmentRules.category_ai_share || {}).Books ?? 0}
                        onChange={(e) => {
                          const val = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                          setAssignmentRules(r => ({
                            ...r,
                            category_ai_share: { ...(r.category_ai_share || {}), Books: val },
                          }));
                        }}
                        className="w-14 px-1.5 py-0.5 mx-1 border border-orange-300 rounded text-center text-orange-900 font-mono"
                        data-testid="books-ai-share-pct"
                      />% of Books queries (during office hours)
                    </label>
                    <p className="text-[11px] text-orange-700/80">
                      Probabilistic — each new Books chat rolls a coin. Outside office hours AI handles 100% anyway.
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-[#1C1917] mb-1">Default Agents (Round-Robin)</h3>
                  <p className="text-xs text-[#57534E] mb-2">Chats with no category are assigned to these agents</p>
                  <AgentTagSelector
                    selected={assignmentRules.default_agents || []}
                    allUsers={allUsers}
                    onChange={(agents) => setAssignmentRules(r => ({ ...r, default_agents: agents }))}
                    testId="default-agents"
                  />
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Currency Exchange Rates — admin only. Yash, Apr 29 2026 */}
        <CurrencyRatesCard user={user} />



        {/* SLA Configuration - Admin Only */}
        {(user?.role === "admin" || user?.role === "super_admin") && (
          <div className="bento-card" data-testid="sla-config-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-md"><Timer size={20} className="text-red-700" /></div>
                <h2 className="text-xl font-medium text-[#1C1917]" style={{ fontFamily: "Playfair Display, serif" }}>SLA Configuration</h2>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={slaConfig.enabled} onChange={(e) => setSlaConfig(s => ({ ...s, enabled: e.target.checked }))} className="w-4 h-4 rounded border-gray-300 text-red-600" data-testid="sla-enabled" />
                  Enabled
                </label>
                <button onClick={saveSlaConfig} disabled={savingSla} className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1" data-testid="save-sla-btn">
                  {savingSla ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Save</>}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">First Response Target (minutes)</label>
                <input type="number" value={slaConfig.first_response_minutes} onChange={(e) => setSlaConfig(s => ({ ...s, first_response_minutes: parseInt(e.target.value) || 0 }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="sla-response-minutes" />
                <p className="text-[10px] text-gray-400 mt-1">Alert if no reply within this time</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Resolution Target (hours)</label>
                <input type="number" value={slaConfig.resolution_hours} onChange={(e) => setSlaConfig(s => ({ ...s, resolution_hours: parseInt(e.target.value) || 0 }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="sla-resolution-hours" />
                <p className="text-[10px] text-gray-400 mt-1">Alert if chat not resolved within this time</p>
              </div>
            </div>
          </div>
        )}


        {/* AI Section — extracted to components/settings/SettingsAiSection.jsx
            (iter 137, Feb 2026). Auto-Reply + Priority Queue + AI Rules + AI Learning. */}
        <SettingsAiSection
          user={user}
          autoReply={autoReply}
          setAutoReply={setAutoReply}
          savingAutoReply={savingAutoReply}
          saveAutoReply={saveAutoReply}
          priorityQueue={priorityQueue}
          togglePriorityQueue={togglePriorityQueue}
          aiRules={aiRules}
          aiRulesText={aiRulesText}
          setAiRulesText={setAiRulesText}
          savingAiRules={savingAiRules}
          handleSaveAiRules={handleSaveAiRules}
          aiKnowledge={aiKnowledge}
          learningInProgress={learningInProgress}
          triggerAiLearning={triggerAiLearning}
        />

        {/* Sales & Promotions — Iter 153.17, Yash Feb 8 2026.
            Admin-managed sale periods that the AI applies automatically. */}
        <SettingsSalesCard user={user} />
      </div>
    </div>
  );
};

export default SettingsPage;

// Helper component for pasting + exchanging Zoho Grant Token
// ZohoAuthForm and RazorpayKeyForm are now defined inside their respective
// card components (SettingsZohoCard / SettingsRazorpayCard) — iter 140.

